import React, { useState, useEffect, useCallback } from 'react';
import {
  Bluetooth,
  BluetoothConnected,
  Play,
  Square,
  Trash2,
  Settings,
  User,
  Users
} from 'lucide-react';
import CalibrationModal from '../../calibration/Calibration';
import SaveSessionModal from '../../storage/saveSession/saveSession';
import { dataService } from '../../storage/dataService';
import './Header.css';

const Header = ({
  devices,
  isConnecting,
  onConnect,
  onDisconnect,
  isRecording,
  handleToggleRecording,
  handleClearData,
  currentUser,
  viewMode = 'dual',
  onViewModeChange,
  selectedFoot = 'left',
  onFootChange,
  onWidgetSelectorToggle,
}) => {
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);

  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [calibrationFoot, setCalibrationFoot] = useState(null);

  const leftConnected = devices?.left?.isConnected || false;
  const rightConnected = devices?.right?.isConnected || false;
  const isAnyConnected = leftConnected || rightConnected;
  const areBothConnected = leftConnected && rightConnected;
  const isAnyRecording = isRecording?.left || isRecording?.right;

  const isSelectedFootConnected = viewMode === 'single'
    ? devices?.[selectedFoot]?.isConnected
    : isAnyConnected;

  const currentRecording = viewMode === 'single'
    ? isRecording?.[selectedFoot]
    : isAnyRecording;

  const handleStart = useCallback(async () => {
    console.log('🎬 handleStart called');

    if (!currentUser?.id) {
      console.error('❌ No user ID');
      alert('Please log in to record sessions');
      return;
    }

    if (!isAnyConnected) {
      console.error('❌ No device connected');
      alert('Please connect a device first');
      return;
    }

    try {
      const tempName = `Recording ${new Date().toISOString()}`;
      const session = await dataService.startSession(currentUser.id, tempName);
      console.log('✅ Session created:', session);

      setIsSessionActive(true);
      setSessionStartTime(Date.now());

      if (viewMode === 'single') {
        console.log(`▶️ Starting recording on ${selectedFoot} foot`);
        handleToggleRecording(selectedFoot, true);
      } else {
        if (leftConnected) {
          console.log('▶️ Starting recording on left foot');
          handleToggleRecording('left', true);
        }
        if (rightConnected) {
          console.log('▶️ Starting recording on right foot');
          handleToggleRecording('right', true);
        }
      }

      console.log('✅ Recording started successfully');
    } catch (err) {
      console.error('❌ Failed to start session:', err);
      alert(`Failed to start recording: ${err.message || 'Unknown error'}`);
    }
  }, [currentUser?.id, isAnyConnected, viewMode, selectedFoot, leftConnected, rightConnected, handleToggleRecording]);

  const handleStop = useCallback(() => {
    if (viewMode === 'single') {
      handleToggleRecording(selectedFoot, false);
    } else {
      handleToggleRecording('left', false);
      handleToggleRecording('right', false);
    }

    const duration = sessionStartTime
      ? Math.floor((Date.now() - sessionStartTime) / 1000)
      : 0;
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;

    setSessionStats({
      duration: `${minutes}:${seconds.toString().padStart(2, '0')}`,
      snapshots: dataService.dataBuffer?.length || 0,
      timeOnPointe: 0
    });

    setShowSaveModal(true);
  }, [viewMode, selectedFoot, handleToggleRecording, sessionStartTime]);

  const handleSaveSession = useCallback(async (sessionName) => {
    setIsSaving(true);

    try {
      const sessionId = dataService.currentSessionId;

      await dataService.endSession();

      if (sessionId) {
        const { supabase } = await import('../../backend/supabaseClient');
        await supabase
          .from('sessions')
          .update({ session_name: sessionName })
          .eq('id', sessionId);
      }

      console.log('✅ Session saved:', sessionName);
      setShowSaveModal(false);
      setIsSessionActive(false);
      setSessionStartTime(null);
      setSessionStats(null);
    } catch (err) {
      console.error('❌ Failed to save session:', err);
      alert('Failed to save session. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, []);

  const handleDiscardSession = useCallback(async () => {
    try {
      const sessionId = dataService.currentSessionId;

      dataService.currentSessionId = null;
      dataService.dataBuffer = [];
      dataService.isActive = false;
      if (dataService.flushInterval) {
        clearInterval(dataService.flushInterval);
        dataService.flushInterval = null;
      }

      if (sessionId) {
        const { supabase } = await import('../../backend/supabaseClient');
        await supabase.from('session_snapshots').delete().eq('session_id', sessionId);
        await supabase.from('sessions').delete().eq('id', sessionId);
      }

      console.log('✅ Session discarded');
      setShowSaveModal(false);
      setIsSessionActive(false);
      setSessionStartTime(null);
      setSessionStats(null);
    } catch (err) {
      console.error('❌ Failed to discard session:', err);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (dataService.isSessionActive()) {
        dataService.endSession().catch(console.error);
      }
    };
  }, []);

  const handleCalibrate = () => {
    if (viewMode === 'single') {
      if (devices?.[selectedFoot]?.isConnected) {
        setCalibrationFoot(selectedFoot);
        setCalibrationOpen(true);
      } else {
        alert(`Please connect the ${selectedFoot} foot device to calibrate`);
      }
    } else {
      if (areBothConnected) {
        setCalibrationFoot('left');
        setCalibrationOpen(true);
      } else if (leftConnected) {
        setCalibrationFoot('left');
        setCalibrationOpen(true);
      } else if (rightConnected) {
        setCalibrationFoot('right');
        setCalibrationOpen(true);
      } else {
        alert('Please connect at least one device to calibrate');
      }
    }
  };

  const handleClear = () => {
    if (viewMode === 'single') {
      handleClearData(selectedFoot);
    } else {
      handleClearData('left');
      handleClearData('right');
    }
  };

  return (
    <>
      <header className="header">
        <div className="header-controls">
          {/* View Mode Toggle */}
          <div className="view-mode-section">
            <div className="view-mode-toggle">
              <button
                className={`view-mode-btn ${viewMode === 'single' ? 'active' : ''}`}
                onClick={() => onViewModeChange?.('single')}
                title="Single Foot Mode"
              >
                <User size={16} />
                <span>Single</span>
              </button>
              <button
                className={`view-mode-btn ${viewMode === 'dual' ? 'active' : ''}`}
                onClick={() => onViewModeChange?.('dual')}
                title="Dual Foot Mode"
              >
                <Users size={16} />
                <span>Both</span>
              </button>
            </div>

            {viewMode === 'single' && (
              <div className="foot-selector">
                <button
                  className={`foot-btn ${selectedFoot === 'left' ? 'active' : ''}`}
                  onClick={() => onFootChange?.('left')}
                >
                  Left
                </button>
                <button
                  className={`foot-btn ${selectedFoot === 'right' ? 'active' : ''}`}
                  onClick={() => onFootChange?.('right')}
                >
                  Right
                </button>
              </div>
            )}
          </div>

          {/* Session Controls */}
          <div className="session-controls">
            <div className="control-buttons-group">
              <button
                onClick={currentRecording ? handleStop : handleStart}
                className={`control-button ${currentRecording ? 'active recording' : ''}`}
                disabled={!isSelectedFootConnected}
                title={currentRecording ? 'Stop Recording' : 'Start Recording'}
              >
                {currentRecording ? <Square size={18} /> : <Play size={18} />}
                <span>{currentRecording ? 'Stop' : 'Start'}</span>
              </button>

              <button
                onClick={handleClear}
                className="control-button"
                disabled={currentRecording}
                title="Clear Data"
              >
                <Trash2 size={18} />
                <span>Clear</span>
              </button>

              <button
                onClick={handleCalibrate}
                className="control-button"
                disabled={!isSelectedFootConnected || currentRecording}
                title="Calibrate Sensors"
              >
                <Settings size={18} />
                <span>Calibrate</span>
              </button>

              {onWidgetSelectorToggle && (
                <button
                  onClick={onWidgetSelectorToggle}
                  className="control-button"
                  title="Customize Widgets"
                >
                  <span>⊞ Widgets</span>
                </button>
              )}
            </div>

            <div className="status-indicators">
              {isSessionActive && (
                <span className="status-indicator session-active">
                  <span className="pulse-dot" />
                  Recording
                </span>
              )}

              {viewMode === 'single' ? (
                devices?.[selectedFoot]?.isConnected && (
                  <span className={`status-indicator ${isRecording?.[selectedFoot] ? 'recording' : ''}`}>
                    {selectedFoot.charAt(0).toUpperCase()}: {isRecording?.[selectedFoot] ? '●' : '○'}
                  </span>
                )
              ) : (
                <>
                  {leftConnected && (
                    <span className={`status-indicator ${isRecording?.left ? 'recording' : ''}`}>
                      L: {isRecording?.left ? '●' : '○'}
                    </span>
                  )}
                  {rightConnected && (
                    <span className={`status-indicator ${isRecording?.right ? 'recording' : ''}`}>
                      R: {isRecording?.right ? '●' : '○'}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Connection Controls */}
          <div className="connection-controls">
            {viewMode === 'single' ? (
              <div className="connection-status">
                {devices?.[selectedFoot]?.isConnected ? (
                  <div className="status connected">
                    <BluetoothConnected size={16} />
                    <span>{devices[selectedFoot].device?.name || `Core ${selectedFoot.charAt(0).toUpperCase()}`}</span>
                    <button
                      onClick={() => onDisconnect(selectedFoot)}
                      className="btn btn-secondary btn-small"
                      disabled={currentRecording}
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <div className="status disconnected">
                    <Bluetooth size={16} />
                    <button
                      onClick={() => onConnect(selectedFoot)}
                      disabled={isConnecting?.[selectedFoot]}
                      className="btn btn-primary btn-small"
                    >
                      {isConnecting?.[selectedFoot] ? 'Connecting...' : `Connect ${selectedFoot.charAt(0).toUpperCase()}`}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="connection-status">
                  {leftConnected ? (
                    <div className="status connected">
                      <BluetoothConnected size={16} />
                      <span>Core L</span>
                      <button
                        onClick={() => onDisconnect('left')}
                        className="btn btn-secondary btn-small"
                        disabled={currentRecording}
                      >
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <div className="status disconnected">
                      <Bluetooth size={16} />
                      <button
                        onClick={() => onConnect('left')}
                        disabled={isConnecting?.left}
                        className="btn btn-primary btn-small"
                      >
                        {isConnecting?.left ? '...' : 'Core L'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="connection-status">
                  {rightConnected ? (
                    <div className="status connected">
                      <BluetoothConnected size={16} />
                      <span>Core R</span>
                      <button
                        onClick={() => onDisconnect('right')}
                        className="btn btn-secondary btn-small"
                        disabled={currentRecording}
                      >
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <div className="status disconnected">
                      <Bluetooth size={16} />
                      <button
                        onClick={() => onConnect('right')}
                        disabled={isConnecting?.right}
                        className="btn btn-primary btn-small"
                      >
                        {isConnecting?.right ? '...' : 'Core R'}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <SaveSessionModal
        open={showSaveModal}
        onSave={handleSaveSession}
        onDiscard={handleDiscardSession}
        onClose={handleDiscardSession}
        sessionStats={sessionStats}
        isLoading={isSaving}
      />

      {calibrationOpen && calibrationFoot && (
        <CalibrationModal
          open={calibrationOpen}
          onClose={() => {
            setCalibrationOpen(false);
            setCalibrationFoot(null);
          }}
          characteristic={devices?.[calibrationFoot]?.characteristic}
          commandCharacteristic={devices?.[calibrationFoot]?.commandCharacteristic}
          deviceId={devices?.[calibrationFoot]?.deviceId}
          foot={calibrationFoot}
        />
      )}
    </>
  );
};

export default Header;
